import { Dialog, Select } from '@rocketlane/ui';
import { useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import type {
  BySalesforceResourceMap,
  SalesforceObjectOption,
} from 'module/api/integrations/salesforce/types';

type ResourceTypeOptions = {
  projects: SalesforceObjectOption[];
  accounts: SalesforceObjectOption[];
  users: SalesforceObjectOption[];
};

type AddResourceModalTypes = {
  resources: BySalesforceResourceMap;
  visibility: boolean;
  resourceTypeOptions: ResourceTypeOptions;
  resourceTypeStatus: 'error' | 'pending' | 'success';
  creationStatus: 'error' | 'pending' | 'success' | 'idle';
  selectedNativeResourceType: 'project' | 'company' | 'user';
  handleClose: () => void;
  handleSubmit: (selected?: string) => void;
};

export const AddResourceModal = ({
  resources,
  resourceTypeOptions,
  creationStatus,
  resourceTypeStatus,
  visibility,
  handleSubmit,
  handleClose,
  selectedNativeResourceType,
}: AddResourceModalTypes) => {
  const [selected, setSelected] = useState<
    SalesforceObjectOption | null | undefined
  >(undefined);

  const selectedResources = useMemo(
    () => Object.values(resources)?.map((value) => value.externalResourceType),
    [resources],
  );

  // Get the appropriate options based on selected native resource type
  const currentOptions = useMemo(() => {
    const optionsMap = {
      project: resourceTypeOptions.projects,
      company: resourceTypeOptions.accounts,
      user: resourceTypeOptions.users,
    };

    return optionsMap[selectedNativeResourceType] || [];
  }, [resourceTypeOptions, selectedNativeResourceType]);

  const filteredOptions = useMemo(() => {
    return currentOptions?.filter(
      (resourceType) =>
        !selectedResources.includes(resourceType.value.toString()),
    );
  }, [currentOptions, selectedResources]);

  const handleSelection = () => {
    handleSubmit(selected?.value);
  };

  return (
    <Dialog
      onAction={handleSelection}
      onCancel={handleClose}
      open={visibility}
      title={
        <FormattedMessage id="settings.integrations.sf.automation.create.object.modal.title" />
      }
      actionProps={{ disabled: resourceTypeStatus === 'pending' }}
      content={
        <Select<SalesforceObjectOption>
          autoFocus
          options={filteredOptions}
          isLoading={resourceTypeStatus === 'pending'}
          isDisabled={creationStatus === 'pending'}
          onChange={setSelected}
          value={selected}
          isClearable
          menuPortalTarget={null}
        />
      }
    />
  );
};
